/**
 * MRI Flow Phase Offset Correction using Static Tissue Detection
 *
 * Based on: Pruitt et al. 2018, Magnetic Resonance in Medicine
 * https://pmc.ncbi.nlm.nih.gov/articles/PMC6372316/
 *
 * ============================================================================
 * PURPOSE
 * ============================================================================
 *
 * Corrects background phase offset (BPO) errors in MRI velocity mapping by:
 * 1. Detecting static tissue (low magnitude, low temporal variance)
 * 2. Fitting 2D polynomial surface to TIME-AVERAGED phase in static tissue
 * 3. Subtracting the SAME fitted offset from ALL cardiac phases
 *
 * ============================================================================
 * TIME-AVERAGED vs PER-FRAME CORRECTION
 * ============================================================================
 *
 * TIME-AVERAGED CORRECTION (implemented here):
 * ✓ Fit polynomial to time-averaged phase image
 * ✓ Apply same correction to all cardiac phases
 * ✓ Assumes BPO is frame-independent (valid for retrospective gating)
 * ✓ More robust to noise (averages out temporal fluctuations)
 * ✓ Standard approach in literature (Pruitt 2018, Walker 1993, Lankhaar 2005)
 *
 * Rationale:
 *   Background phase errors from eddy currents, gradient non-linearity, and
 *   concomitant fields are HARDWARE-RELATED and do not change with cardiac
 *   phase when using retrospective gating. Time-averaging removes pulsatile
 *   flow signal while preserving the underlying static offset field.
 *
 * PER-FRAME CORRECTION (not recommended for cardiac cine):
 *   - Fit separate polynomial to each frame
 *   - Different correction per frame
 *   - Only needed for prospectively-gated or real-time acquisitions
 *   - Risk of overcorrection (fitting to flow signal, not just offset)
 *
 * ============================================================================
 * PHYSICAL BACKGROUND
 * ============================================================================
 *
 * Phase contrast MRI measures velocity by encoding it into phase:
 *   φ = (π·v) / venc
 *
 * Ideally, static tissue (v=0) should have φ=0
 *
 * In practice, background phase errors arise from:
 *   - Eddy currents (main source)
 *   - Gradient non-linearity
 *   - Concomitant field gradients
 *   - B0 inhomogeneity
 *
 * These errors:
 *   - Vary smoothly across space (polynomial-like)
 *   - Are temporally stable for retrospective gating
 *   - Can cause large flow quantification errors (>5% stroke volume)
 *
 * ============================================================================
 * ALGORITHM
 * ============================================================================
 *
 * 1. Static Tissue Detection (temporal analysis across all frames):
 *    - Low magnitude (bottom percentile of time-averaged magnitude)
 *    - Low temporal std dev (stable phase across cardiac cycle)
 *    - Excludes blood vessels (high temporal variation) and moving structures
 *
 * 2. Time-Averaged Phase Calculation:
 *    - Compute mean phase: φ̄(x,y) = (1/N) Σ_t φ_t(x,y)
 *    - Pulsatile flow averages to zero (positive/negative balance)
 *    - Static offset remains (temporally constant)
 *
 * 3. Polynomial Fitting to Time-Averaged Phase:
 *    - 1st order: φ_offset(x,y) = a₀ + a₁x + a₂y
 *    - 2nd order: φ_offset(x,y) = a₀ + a₁x + a₂y + a₃x² + a₄xy + a₅y²
 *    - 3rd order: 10 coefficients (includes cubic terms)
 *    - Fit only to static tissue pixels
 *
 * 4. Correction (applied identically to all frames):
 *    - φ_corrected,t(x,y) = φ_t(x,y) - φ_offset(x,y)
 *    - Same offset surface subtracted from each cardiac phase
 */

export interface StaticTissueThresholds {
  /**
   * Magnitude percentile threshold (0-1)
   *
   * Uses pixels below this percentile of mean magnitude
   *
   * Typical values:
   *   - 0.2 (20%): Aggressive - more static tissue candidates
   *   - 0.3 (30%): Balanced - recommended default
   *   - 0.4 (40%): Conservative - fewer candidates
   *   - 0.99 (99%): Effectively disabled (use temporal std only)
   *
   * Higher values include more pixels but risk including slow flow
   */
  magnitudePercentile: number;

  /**
   * Temporal standard deviation threshold (velocity units, cm/s)
   *
   * Maximum std dev of phase across cardiac cycle for static tissue
   *
   * Physical interpretation:
   *   - Measures velocity fluctuation across cardiac cycle
   *   - Static tissue: std dev ≈ noise level (< 1 cm/s)
   *   - Slow flow: std dev 2-10 cm/s
   *   - Pulsatile vessels: std dev > 10 cm/s
   *
   * Typical values (for venc=150 cm/s):
   *   - 1.0 cm/s: Very tight - only very stable tissue
   *   - 2.0-5.0 cm/s: Balanced - recommended range
   *   - 10.0 cm/s: Loose - includes slow flow
   *   - 100.0 cm/s: Effectively disabled (use magnitude only)
   *
   * Should be << venc (typically 1-5% of venc)
   */
  temporalStdThreshold: number;

  /**
   * Minimum number of static tissue pixels required for fitting
   *
   * Too few pixels → unreliable polynomial fit
   *
   * Typical values:
   *   - 30: Minimum for basic fit
   *   - 50-100: Balanced - recommended default
   *   - 200: Conservative - ensures robust fit
   *
   * Depends on image size and polynomial order:
   *   - 1st order: needs ≥ 10 pixels
   *   - 2nd order: needs ≥ 30 pixels
   *   - 3rd order: needs ≥ 100 pixels
   */
  minPixelCount: number;
}

export interface ImageBounds {
  /**
   * Left boundary offset (pixels from left edge)
   * Excludes pixels with x < leftOffset
   */
  leftOffset: number;

  /**
   * Right boundary offset (pixels from right edge)
   * Excludes pixels with x >= (width - rightOffset)
   */
  rightOffset: number;

  /**
   * Top boundary offset (pixels from top edge)
   * Excludes pixels with y < topOffset
   */
  topOffset: number;

  /**
   * Bottom boundary offset (pixels from bottom edge)
   * Excludes pixels with y >= (height - bottomOffset)
   */
  bottomOffset: number;
}

export interface CorrectionResult {
  /**
   * Corrected phase frames (cm/s)
   * Same offset applied to all frames
   */
  correctedFrames: Float32Array[];

  /**
   * Static tissue mask (1=static tissue, 0=excluded)
   * Detected from temporal analysis across all frames
   */
  staticTissueMask: Uint8Array;

  /**
   * Polynomial coefficients (single set for all frames)
   * Fit to time-averaged phase image
   */
  polynomialCoefficients: number[];

  /**
   * Fitted offset surface (cm/s)
   * Single surface applied to all frames
   */
  offsetSurface: Float32Array;

  /**
   * Quality metrics
   */
  metrics: {
    staticTissueCount: number; // Number of pixels used for fitting
    rmsError: number; // RMS fit error on time-averaged phase (cm/s)
    meanOffset: number; // Mean offset value (cm/s)
    maxOffset: number; // Maximum offset magnitude (cm/s)
    timeAveragedPhaseRange: {
      // Range of time-averaged phase
      min: number;
      max: number;
    };
  };

  /**
   * Exclusion masks for visualization
   */
  excludedMasks?: {
    lowMagnitude: Uint8Array;
    highTemporalStd: Uint8Array;
    boundsExcluded: Uint8Array;
    combined: Uint8Array;
  };
  appliedBounds?: ImageBounds;
}

export class FlowPhaseOffsetCorrection {
  // ============================================================================
  // PUBLIC PROPERTIES
  // ============================================================================

  width: number;
  height: number;
  venc: number;
  polynomialOrder: number;

  // ============================================================================
  // PRIVATE CONFIGURATION
  // ============================================================================

  private _thresholds: StaticTissueThresholds;
  private _bounds: ImageBounds;
  private _logDiagnostics = true;

  // ============================================================================
  // CONSTRUCTOR
  // ============================================================================

  constructor(opts: {
    width: number;
    height: number;
    venc: number;
    polynomialOrder?: number;
    thresholds?: Partial<StaticTissueThresholds>;
    bounds?: Partial<ImageBounds>;
  }) {
    const { width, height, venc } = opts;

    if (
      !Number.isFinite(width)
      || !Number.isFinite(height)
      || width <= 0
      || height <= 0
    ) {
      throw new Error('width and height must be positive numbers');
    }
    if (!Number.isFinite(venc) || venc <= 0) {
      throw new Error('venc must be positive');
    }

    this.width = Math.trunc(width);
    this.height = Math.trunc(height);
    this.venc = venc;
    this.polynomialOrder = opts.polynomialOrder ?? 2;

    if (![1, 2, 3].includes(this.polynomialOrder)) {
      throw new Error('polynomialOrder must be 1, 2, or 3');
    }

    // Default thresholds
    this._thresholds = {
      magnitudePercentile: 0.3,
      temporalStdThreshold: 0.1,
      minPixelCount: 100,
      ...opts.thresholds,
    };

    // Default bounds: no exclusion (use full image)
    this._bounds = {
      leftOffset: 0,
      rightOffset: 0,
      topOffset: 0,
      bottomOffset: 0,
      ...opts.bounds,
    };
  }

  // ============================================================================
  // PUBLIC API
  // ============================================================================

  setThresholds(thresholds: Partial<StaticTissueThresholds>) {
    this._thresholds = { ...this._thresholds, ...thresholds };
  }

  getThresholds(): StaticTissueThresholds {
    return { ...this._thresholds };
  }

  setBounds(bounds: Partial<ImageBounds>) {
    this._bounds = { ...this._bounds, ...bounds };
  }

  getBounds(): ImageBounds {
    return { ...this._bounds };
  }

  /**
   * Main correction method - Time-Averaged Approach
   *
   * ========================================================================
   * ALGORITHM OVERVIEW
   * ========================================================================
   *
   * 1. Detect static tissue using temporal statistics:
   *    - Analyze ALL frames to identify stable pixels
   *    - Criteria: low magnitude AND low temporal std dev
   *
   * 2. Compute time-averaged phase image:
   *    - φ̄(x,y) = (1/N) Σ_t=1^N φ_t(x,y)
   *    - Pulsatile flow: averages to ~0 (equal forward/backward)
   *    - Static offset: preserved (temporally constant)
   *
   * 3. Fit polynomial surface to time-averaged phase:
   *    - Use only static tissue pixels
   *    - Extrapolate to full field-of-view
   *    - Single fit for entire cardiac cycle
   *
   * 4. Apply same correction to all frames:
   *    - φ_corrected,t = φ_t - φ_offset
   *    - Preserves temporal dynamics (flow waveform shape)
   *    - Removes systematic offset
   *
   * ========================================================================
   * WHY TIME-AVERAGED?
   * ========================================================================
   *
   * For retrospectively-gated cardiac cine MRI:
   *
   * ✓ BPO is hardware-related → temporally stable across cardiac phases
   * ✓ Same eddy currents, gradient non-linearity for all frames
   * ✓ Time-averaging cancels pulsatile flow (signal) but preserves BPO
   * ✓ More robust fit (noise reduction from averaging)
   * ✓ Prevents fitting to flow signal (avoids overcorrection)
   *
   * Assumption: BPO(x,y,t) = BPO(x,y) for all t
   *   Valid for retrospective ECG gating
   *   Invalid for real-time or prospective gating (use per-frame then)
   *
   * @param phaseFrames - Phase data in cm/s (velocity units)
   * @param magnitudeFrames - Magnitude data (arbitrary units, for tissue detection)
   * @returns CorrectionResult with single offset surface applied to all frames
   */
  correctPhaseOffset(
    phaseFrames: Array<Float32Array | Uint16Array>,
    magnitudeFrames: Array<Float32Array | Uint16Array>,
  ): CorrectionResult {
    if (phaseFrames.length !== magnitudeFrames.length) {
      throw new Error('Phase and magnitude frame counts must match');
    }
    if (phaseFrames.length === 0) {
      throw new Error('No frames provided');
    }

    const numFrames = phaseFrames.length;
    const n = this.width * this.height;

    // Convert to Float32Array if needed
    const phaseFloat = phaseFrames.map(f =>
      FlowPhaseOffsetCorrection._toFloat32(f),
    );
    const magFloat = magnitudeFrames.map(f =>
      FlowPhaseOffsetCorrection._toFloat32(f),
    );

    console.log(
      `Phase offset correction (time-averaged): ${numFrames} frames, ${this.width}x${this.height}, order=${this.polynomialOrder}`,
    );

    // -------------------------------------------------------------------------
    // STEP 1: Detect static tissue (temporal analysis across ALL frames)
    // -------------------------------------------------------------------------
    const detection = this._detectStaticTissue(phaseFloat, magFloat);
    const staticTissueMask = detection.mask;

    // Apply image bounds to restrict detection area
    const boundsExcludedMask = this._applyImageBounds(staticTissueMask);

    const staticCount = staticTissueMask.reduce((sum, val) => sum + val, 0);
    const boundsExcludedCount = boundsExcludedMask.reduce(
      (sum, val) => sum + val,
      0,
    );
    console.log(
      `Static tissue detected: ${staticCount} pixels (${(
        (100 * staticCount)
        / n
      ).toFixed(1)}%)`,
    );
    if (boundsExcludedCount > 0) {
      console.log(`  Excluded by bounds: ${boundsExcludedCount} pixels`);
    }

    if (staticCount < this._thresholds.minPixelCount) {
      console.warn(
        `Insufficient static tissue pixels (${staticCount} < ${this._thresholds.minPixelCount}). `
        + `Correction may be unreliable. Try adjusting thresholds.`,
      );
    }

    // -------------------------------------------------------------------------
    // STEP 2: Compute time-averaged phase image
    // -------------------------------------------------------------------------
    /**
     * Time-averaging rationale:
     *
     * For pulsatile flow in cardiac cycle:
     *   - Systole: positive velocity (forward flow)
     *   - Diastole: negative velocity (backward flow)
     *   - Time average: ≈ 0 (flow cancels out)
     *
     * For static tissue with BPO:
     *   - All phases: constant offset φ_offset(x,y)
     *   - Time average: φ_offset (preserved)
     *
     * Mathematical expression:
     *   φ_measured,t(x,y) = φ_flow,t(x,y) + φ_offset(x,y)
     *
     *   Time average:
     *   φ̄(x,y) = (1/N) Σ_t φ_measured,t(x,y)
     *          = (1/N) Σ_t φ_flow,t(x,y) + φ_offset(x,y)
     *          ≈ 0 + φ_offset(x,y)
     *
     * In static tissue: φ_flow = 0 exactly
     *   → φ̄_static(x,y) = φ_offset(x,y)
     */

    const timeAvgPhase = new Float32Array(n);
    for (let i = 0; i < n; i++) {
      let sum = 0;
      for (let t = 0; t < numFrames; t++) {
        sum += phaseFloat[t][i];
      }
      timeAvgPhase[i] = sum / numFrames;
    }

    // Compute range for diagnostics
    let avgMin = Infinity;
    let avgMax = -Infinity;
    for (let i = 0; i < n; i++) {
      if (timeAvgPhase[i] < avgMin) avgMin = timeAvgPhase[i];
      if (timeAvgPhase[i] > avgMax) avgMax = timeAvgPhase[i];
    }

    if (this._logDiagnostics) {
      console.log(
        `Time-averaged phase range: [${avgMin.toFixed(4)}, ${avgMax.toFixed(
          4,
        )}] cm/s`,
      );
    }

    // -------------------------------------------------------------------------
    // STEP 3: Fit polynomial surface to time-averaged phase
    // -------------------------------------------------------------------------
    /**
     * Single polynomial fit for entire cardiac cycle
     *
     * Input: Time-averaged phase in static tissue pixels
     * Output: Polynomial coefficients describing offset field
     *
     * Extrapolation: Polynomial extends beyond static tissue to full FOV
     *   - Necessary for correcting vessel regions
     *   - Assumes smooth spatial variation of offset
     */

    const fitResult = this._fitPolynomial2D(timeAvgPhase, staticTissueMask);
    const offsetSurface = this._evaluatePolynomial2D(fitResult.coefficients);

    console.log(
      `Polynomial fit (order ${this.polynomialOrder}): `
      + `RMS=${fitResult.rmsError.toFixed(4)} cm/s, `
      + `${staticCount} pixels`,
    );

    // -------------------------------------------------------------------------
    // STEP 4: Apply SAME correction to ALL frames
    // -------------------------------------------------------------------------
    /**
     * Uniform correction across cardiac cycle
     *
     * Each frame corrected identically:
     *   φ_corrected,t(x,y) = φ_measured,t(x,y) - φ_offset(x,y)
     *
     * This preserves:
     *   ✓ Temporal dynamics (flow waveform shape unchanged)
     *   ✓ Relative phase differences between frames
     *   ✓ Flow quantification accuracy
     *
     * Removes:
     *   ✓ Systematic background offset
     *   ✓ Spatial gradient in static tissue
     *   ✓ Eddy current artifacts
     */

    const correctedFrames: Float32Array[] = [];
    for (let t = 0; t < numFrames; t++) {
      const corrected = new Float32Array(n);
      for (let i = 0; i < n; i++) {
        corrected[i] = phaseFloat[t][i] - offsetSurface[i];
      }
      correctedFrames.push(corrected);
    }

    // -------------------------------------------------------------------------
    // STEP 5: Compute quality metrics
    // -------------------------------------------------------------------------

    let sumOffset = 0;
    let maxOffset = 0;
    for (let i = 0; i < n; i++) {
      const offset = Math.abs(offsetSurface[i]);
      sumOffset += offsetSurface[i];
      if (offset > maxOffset) maxOffset = offset;
    }

    const metrics = {
      staticTissueCount: staticCount,
      rmsError: fitResult.rmsError,
      meanOffset: sumOffset / n,
      maxOffset,
      timeAveragedPhaseRange: {
        min: avgMin,
        max: avgMax,
      },
    };

    if (this._logDiagnostics) {
      console.log(
        `Offset field: mean=${metrics.meanOffset.toFixed(4)} cm/s, `
        + `max=${metrics.maxOffset.toFixed(4)} cm/s`,
      );
    }

    // Combine exclusion masks
    const combinedExclusion = new Uint8Array(n);
    for (let i = 0; i < n; i++) {
      combinedExclusion[i]
        = detection.exclusions.lowMagnitude[i]
          || detection.exclusions.highTemporalStd[i]
          ? 1
          : 0;
    }

    return {
      correctedFrames,
      staticTissueMask,
      polynomialCoefficients: fitResult.coefficients, // Single set of coefficients
      offsetSurface, // Single offset surface
      metrics,
      excludedMasks: {
        lowMagnitude: detection.exclusions.lowMagnitude,
        highTemporalStd: detection.exclusions.highTemporalStd,
        boundsExcluded: boundsExcludedMask,
        combined: combinedExclusion,
      },
      appliedBounds: { ...this._bounds },
    };
  }

  // ============================================================================
  // STATIC TISSUE DETECTION
  // ============================================================================

  /**
   * Detect static tissue using temporal statistics
   *
   * Analyzes all frames to identify pixels with:
   * 1. Low time-averaged magnitude (excludes bright vessels)
   * 2. Low temporal std dev of phase (excludes pulsatile flow)
   *
   * Both criteria must be met for pixel to be classified as static tissue
   */
  private _detectStaticTissue(
    phaseFrames: Float32Array[],
    magnitudeFrames: Float32Array[],
  ): {
    mask: Uint8Array;
    exclusions: {
      lowMagnitude: Uint8Array;
      highTemporalStd: Uint8Array;
    };
  } {
    const n = this.width * this.height;
    const numFrames = phaseFrames.length;
    const mask = new Uint8Array(n);

    const lowMagnitudeMask = new Uint8Array(n);
    const highTemporalStdMask = new Uint8Array(n);

    // -----------------------------------------------------------------------
    // CRITERION 1: Low time-averaged magnitude
    // -----------------------------------------------------------------------

    const meanMagnitude = new Float32Array(n);
    for (let i = 0; i < n; i++) {
      let sum = 0;
      for (let t = 0; t < numFrames; t++) {
        sum += magnitudeFrames[t][i];
      }
      meanMagnitude[i] = sum / numFrames;
    }

    const sortedMag = Array.from(meanMagnitude).sort((a, b) => a - b);
    const magThresholdIndex = Math.min(
      Math.floor(n * this._thresholds.magnitudePercentile),
      n - 1,
    );
    const magThreshold = sortedMag[magThresholdIndex];

    if (this._logDiagnostics) {
      console.log(
        `Magnitude threshold (${(
          this._thresholds.magnitudePercentile * 100
        ).toFixed(0)}th percentile): ${magThreshold.toFixed(2)}`,
      );
    }

    // -----------------------------------------------------------------------
    // CRITERION 2: Low temporal standard deviation of phase
    // -----------------------------------------------------------------------

    const phaseStd = new Float32Array(n);
    for (let i = 0; i < n; i++) {
      // Compute mean phase
      let mean = 0;
      for (let t = 0; t < numFrames; t++) {
        mean += phaseFrames[t][i];
      }
      mean /= numFrames;

      // Compute standard deviation
      let variance = 0;
      for (let t = 0; t < numFrames; t++) {
        const diff = phaseFrames[t][i] - mean;
        variance += diff * diff;
      }
      phaseStd[i] = Math.sqrt(variance / numFrames);
    }

    if (this._logDiagnostics) {
      console.log(
        `Temporal std threshold: ${this._thresholds.temporalStdThreshold.toFixed(
          2,
        )} cm/s`,
      );
    }

    // -----------------------------------------------------------------------
    // COMBINE CRITERIA
    // -----------------------------------------------------------------------

    let magOnlyCount = 0;
    let stdOnlyCount = 0;
    let bothCount = 0;

    for (let i = 0; i < n; i++) {
      const lowMag = meanMagnitude[i] < magThreshold;
      const lowStd = phaseStd[i] < this._thresholds.temporalStdThreshold;

      if (lowMag) magOnlyCount++;
      if (lowStd) stdOnlyCount++;

      // Track exclusions
      if (!lowMag) lowMagnitudeMask[i] = 1;
      if (!lowStd) highTemporalStdMask[i] = 1;

      // Include only if BOTH criteria met
      if (lowMag && lowStd) {
        mask[i] = 1;
        bothCount++;
      }
    }

    if (this._logDiagnostics) {
      console.log(
        `Candidates: lowMag=${magOnlyCount}, lowStd=${stdOnlyCount}, both=${bothCount}`,
      );
    }

    return {
      mask,
      exclusions: {
        lowMagnitude: lowMagnitudeMask,
        highTemporalStd: highTemporalStdMask,
      },
    };
  }

  /**
   * Apply image bounds to restrict static tissue detection area
   *
   * Modifies mask IN-PLACE to exclude pixels outside user-defined bounds
   * Useful for excluding wrap-around artifacts or vessel-heavy regions
   *
   * @param mask - Static tissue mask (modified in-place)
   * @returns Mask of pixels excluded by bounds (for visualization)
   */
  private _applyImageBounds(mask: Uint8Array): Uint8Array {
    const w = this.width;
    const h = this.height;
    const excludedMask = new Uint8Array(w * h);

    const xMin = this._bounds.leftOffset;
    const xMax = w - this._bounds.rightOffset;
    const yMin = this._bounds.topOffset;
    const yMax = h - this._bounds.bottomOffset;

    let excludedCount = 0;

    for (let y = 0; y < h; y++) {
      for (let x = 0; x < w; x++) {
        const i = y * w + x;

        // Check if pixel is outside bounds
        if (x < xMin || x >= xMax || y < yMin || y >= yMax) {
          if (mask[i] === 1) {
            excludedCount++;
          }

          mask[i] = 0; // Exclude from static tissue (modifies in-place)
          excludedMask[i] = 1; // Track for visualization
        }
      }
    }

    if (this._logDiagnostics && excludedCount > 0) {
      console.log(
        `  Bounds excluded ${excludedCount} static tissue pixels `
        + `[L:${xMin}, R:${this._bounds.rightOffset}, T:${yMin}, B:${this._bounds.bottomOffset}]`,
      );
    }

    return excludedMask;
  }

  // ============================================================================
  // POLYNOMIAL FITTING
  // ============================================================================

  private _fitPolynomial2D(
    phaseData: Float32Array,
    mask: Uint8Array,
  ): { coefficients: number[]; rmsError: number } {
    const w = this.width;
    const h = this.height;

    // Collect static tissue pixels
    const points: Array<{ x: number; y: number; phase: number }> = [];
    for (let y = 0; y < h; y++) {
      for (let x = 0; x < w; x++) {
        const i = y * w + x;
        if (mask[i]) {
          points.push({ x, y, phase: phaseData[i] });
        }
      }
    }

    if (points.length < this._thresholds.minPixelCount) {
      console.warn(
        `Insufficient static tissue pixels for fitting: ${points.length}`,
      );
      const numCoeffs = FlowPhaseOffsetCorrection._getNumCoefficients(
        this.polynomialOrder,
      );
      return { coefficients: Array.from<number>({ length: numCoeffs }).fill(0), rmsError: 0 };
    }

    // Normalize coordinates to [-1, 1]
    const xNorm = w / 2;
    const yNorm = h / 2;

    // Build design matrix and target vector
    const A: number[][] = [];
    const b: number[] = [];

    for (let i = 0; i < points.length; i++) {
      const p = points[i];
      const xn = (p.x - xNorm) / xNorm;
      const yn = (p.y - yNorm) / yNorm;
      A.push(
        FlowPhaseOffsetCorrection._polynomialBasis(xn, yn, this.polynomialOrder),
      );
      b.push(p.phase);
    }

    // Solve least squares: A^T A x = A^T b
    const coefficients = FlowPhaseOffsetCorrection._solveLeastSquares(A, b);

    // Compute RMS error
    let rmsError = 0;
    for (let i = 0; i < points.length; i++) {
      const p = points[i];
      const xn = (p.x - xNorm) / xNorm;
      const yn = (p.y - yNorm) / yNorm;
      const basis = FlowPhaseOffsetCorrection._polynomialBasis(
        xn,
        yn,
        this.polynomialOrder,
      );

      let predicted = 0;
      for (let j = 0; j < coefficients.length; j++) {
        predicted += coefficients[j] * basis[j];
      }

      const error = p.phase - predicted;
      rmsError += error * error;
    }
    rmsError = Math.sqrt(rmsError / points.length);

    return { coefficients, rmsError };
  }

  // ============================================================================
  // POLYNOMIAL EVALUATION
  // ============================================================================

  private _evaluatePolynomial2D(coefficients: number[]): Float32Array {
    const w = this.width;
    const h = this.height;
    const n = w * h;
    const surface = new Float32Array(n);

    const xNorm = w / 2;
    const yNorm = h / 2;

    for (let y = 0; y < h; y++) {
      for (let x = 0; x < w; x++) {
        const i = y * w + x;
        const xn = (x - xNorm) / xNorm;
        const yn = (y - yNorm) / yNorm;

        const basis = FlowPhaseOffsetCorrection._polynomialBasis(
          xn,
          yn,
          this.polynomialOrder,
        );

        let value = 0;
        for (let j = 0; j < coefficients.length; j++) {
          value += coefficients[j] * basis[j];
        }

        surface[i] = value;
      }
    }

    return surface;
  }

  // ============================================================================
  // POLYNOMIAL BASIS FUNCTIONS
  // ============================================================================

  private static _polynomialBasis(
    x: number,
    y: number,
    order: number,
  ): number[] {
    if (order === 1) return [1, x, y];
    if (order === 2) return [1, x, y, x * x, x * y, y * y];
    if (order === 3) {
      return [
        1,
        x,
        y,
        x * x,
        x * y,
        y * y,
        x * x * x,
        x * x * y,
        x * y * y,
        y * y * y,
      ];
    }
    throw new Error(`Unsupported polynomial order: ${order}`);
  }

  private static _getNumCoefficients(order: number): number {
    if (order === 1) return 3;
    if (order === 2) return 6;
    if (order === 3) return 10;
    throw new Error(`Unsupported polynomial order: ${order}`);
  }

  // ============================================================================
  // LEAST SQUARES SOLVER
  // ============================================================================

  private static _solveLeastSquares(A: number[][], b: number[]): number[] {
    const m = A.length;
    const n = A[0].length;

    // Compute A^T·A (Gram matrix)
    const ATA: number[][] = [];
    for (let i = 0; i < n; i++) {
      ATA[i] = Array.from<number>({ length: n }).fill(0);
      for (let j = 0; j < n; j++) {
        for (let k = 0; k < m; k++) {
          ATA[i][j] += A[k][i] * A[k][j];
        }
      }
    }

    // Compute A^T·b
    const ATb = Array.from<number>({ length: n }).fill(0);
    for (let i = 0; i < n; i++) {
      for (let k = 0; k < m; k++) {
        ATb[i] += A[k][i] * b[k];
      }
    }

    // Solve using Gaussian elimination
    return FlowPhaseOffsetCorrection._gaussianElimination(ATA, ATb);
  }

  private static _gaussianElimination(A: number[][], b: number[]): number[] {
    const n = b.length;

    // Create augmented matrix [A|b]
    const aug: number[][] = [];
    for (let i = 0; i < n; i++) {
      aug[i] = [...A[i], b[i]];
    }

    // Forward elimination with partial pivoting
    for (let col = 0; col < n; col++) {
      // Find pivot
      let maxRow = col;
      for (let row = col + 1; row < n; row++) {
        if (Math.abs(aug[row][col]) > Math.abs(aug[maxRow][col])) {
          maxRow = row;
        }
      }

      // Swap rows
      [aug[col], aug[maxRow]] = [aug[maxRow], aug[col]];

      // Check for singular matrix
      if (Math.abs(aug[col][col]) < 1e-12) {
        console.warn('Nearly singular matrix in least squares solve');
        return Array.from<number>({ length: n }).fill(0);
      }

      // Eliminate column
      for (let row = col + 1; row < n; row++) {
        const factor = aug[row][col] / aug[col][col];
        for (let j = col; j <= n; j++) {
          aug[row][j] -= factor * aug[col][j];
        }
      }
    }

    // Back substitution
    const x: number[] = Array.from({ length: n });
    for (let i = n - 1; i >= 0; i--) {
      let sum = aug[i][n];
      for (let j = i + 1; j < n; j++) {
        sum -= aug[i][j] * x[j];
      }
      x[i] = sum / aug[i][i];
    }

    return x;
  }

  // ============================================================================
  // UTILITIES
  // ============================================================================

  private static _toFloat32(input: Float32Array | Uint16Array): Float32Array {
    if (input instanceof Float32Array) {
      return input;
    }
    return new Float32Array(input);
  }
}
